import { ErrorMessage, Field, Form, Formik } from "formik";
import React, { useEffect, useState } from "react";
import { MdOutlineChevronRight } from "react-icons/md";
import { PiShareFat, PiShareFatFill } from "react-icons/pi"; // thicker share icon
import * as Yup from "yup";
import CreateCompanyFooter from "../components/createcompanyfooter/CreateCompanyFooter";
import LinkNavbar from "../components/linksnavbar/LinkNavbar";
import { Link, useLocation } from "react-router-dom";
import Progressbar from "./Progressbar";
import { getApiBaseUrl } from "../apiBaseUrlFinder";
import { toast } from "react-toastify";

const CompanyProfile = () => {
  const validationSchema = Yup.object().shape({
    email: Yup.string().email("Invalid email format.").required("Email is required."),
  });

  // Initial Values
  const initialValues = { email: "" };

  // Form Submission Handler (kept, but Subscribe won't call it)
  const handleSubmit = (values) => {
    console.log("Form Data Submitted: ", values);
  };

  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);

  const [companyData, setCompanyData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const companyId = queryParams.get("id");
    const companyName = queryParams.get("name");

    if (companyId && companyName) {
      const fetchCompanyData = async () => {
        setLoading(true);
        try {
          const params = new URLSearchParams({
            company_id: companyId,
            company_name: companyName,
          });
          const response = await fetch(`${getApiBaseUrl()}/company_profile?${params.toString()}`);
          if (!response.ok) throw new Error("Failed to fetch data");
          const data = await response.json();
          setCompanyData(data);
        } catch (err) {
          setError(err.message);
        } finally {
          setLoading(false);
        }
      };

      fetchCompanyData();
    }
  }, [location.search]);

  // -------- Share interactions --------
  const [copied, setCopied] = useState(false);

  // lightweight flag/tooltip toast
  const showFlag = (message) =>
    toast(message, {
      position: "top-center",
      autoClose: 1600,
      hideProgressBar: true,
      closeButton: false,
      draggable: false,
      className: "flag-toast",
      bodyClassName: "flag-toast-body",
      icon: <span className="flag-dot" />,
    });

  const handleCopy = () => {
    navigator.clipboard
      .writeText(window.location.href)
      .then(() => {
        setCopied(true);
        showFlag("Profile link copied");
        setTimeout(() => setCopied(false), 2500);
      })
      .catch((err) => console.error("Failed to copy:", err));
  };

  // Coming soon handler for email + subscribe
  let flagShown = false;
  const handleComingSoon = (e) => {
    if (e?.preventDefault) e.preventDefault();
    if (!flagShown) {
      flagShown = true;
      showFlag("This feature is coming soon!");
      setTimeout(() => { flagShown = false; }, 1500); // reset after 1.5s
    }
    return false;
  };

  return (
    <div>
      {/* Inline styles: icon micro-interactions + flag toast + manager name link */}
      <style>
        {`
          /* Icon micro-interactions */
          .icon-btn{
            display:inline-flex;
            align-items:center;
            justify-content:center;
            transition: transform .15s ease, opacity .15s ease, filter .15s ease;
          }
          .icon-btn:hover{ transform: translateY(-2px); }
          .icon-btn:active{ transform: translateY(-.5px) scale(.96); }

          /* Flag toast: white card w/ green accent */
          .flag-toast {
            background:#fff !important;
            color:#111 !important;
            border-left:4px solid #16a34a !important;
            border-radius:10px !important;
            box-shadow:0 6px 18px rgba(0,0,0,.12) !important;
            padding:.75rem 1rem !important;
            min-width: 220px;
          }
          .flag-toast-body { font-weight:600; }
          .flag-dot {
            width:10px; height:10px; display:inline-block;
            border-radius:50%; background:#16a34a; margin-right:.5rem;
          }

          /* Manager name link: same size as table cells, bold, darken on hover */
          .manager-name-link{
            display:inline;
            font-size: 13px;
            line-height: inherit;
            font-weight: 600;
            color: inherit;
            text-decoration: underline;
            text-underline-offset: 2px;
            transition: color .15s ease, text-decoration-thickness .15s ease;
          }
          .manager-name-link:hover{
            color:#111;
            text-decoration-thickness: 2px;
          }
          .manager-name-link:active{ opacity:.85; }
          .manager-name-link:focus-visible{
            outline:2px solid #111;
            outline-offset:2px;
            border-radius:4px;
          }

          @media (prefers-reduced-motion: reduce){
            .icon-btn, .manager-name-link { transition:none; }
          }
        `}
      </style>

      {/* <LinkNavbar /> */}
      <div className="container">
        <div className="row pt-lg-5 pt-md-4 pt-3">
          <div className="col-lg-6 col-md-6 col-12">
            <div className="d-flex align-items-end">
              <h3 className="rating_review_h3 mb-0">
                {companyData?.company_profile?.company_rating}
              </h3>
              <h6 className="add_review_rate_h3 ps-2">/ 5</h6>
            </div>

            <p className="company_profile_rating_p pt-2 mb-0">
              {" "}
              <Link
                to={`/company-reviews?name=${encodeURIComponent(
                  companyData?.company_profile?.company_name || ""
                )}&id=${companyData?.company_profile?.company_id || ""}`}
                className="cta-link ratings-cta"
              >
                {companyData?.company_profile?.all_review_count}
                <span> ratings</span>
              </Link>
            </p>
            <Link
              to={`/view-all-managers?name=${encodeURIComponent(
                companyData?.company_profile?.company_name || ""
              )}&id=${companyData?.company_profile?.company_id || ""}`}
              className="view_all_members_text cta-link view-all-cta"
            >
              <strong>{companyData?.company_profile?.manager_count}</strong> Managers
            </Link>
            <div>
              <div className="d-flex align-items-center pt-2">
                {companyData?.company_profile?.company_logo_url && (
                  <img
                    src={companyData.company_profile.company_logo_url}
                    alt={`${companyData.company_profile.company_name} Logo`}
                    style={{
                      width: "55px",
                      height: "55px",
                      objectFit: "cover",
                      borderRadius: "8px",
                      marginRight: "12px",
                    }}
                  />
                )}
                <div>
                  <h3 className="company_management_h3 mb-1">
                    {companyData?.company_profile?.company_name}
                  </h3>
                  <h6 className="company_management_p ps-2">Management</h6>
                </div>
              </div>
            </div>
            <div className="">
              <p className="company_profile_rating_p pt-3 mb-0">
                Work at {companyData?.company_profile?.company_name}?
              </p>
              <p className="company_profile_rating_p">
                Rate your manager <span className="text-black fw-bold">anonymously!</span>
              </p>
            </div>
            <div className="d-flex align-items-center">
              <Link to="/add-review" className="">
                <button className="rate_company_profile_btn">
                  Rate
                  <MdOutlineChevronRight className="ms-1 fs-5" />
                </button>
              </Link>

              {/* Right-side actions: Share only */}
              <div className="d-flex ms-3">
                <div
                  onClick={handleCopy}
                  className="icon-btn"
                  style={{ cursor: "pointer" }}
                  title={copied ? "Link copied" : "Copy link"}
                  aria-label={copied ? "Link copied" : "Copy link"}
                >
                  {copied ? (
                    <PiShareFatFill fontSize={25} color="#151515" />
                  ) : (
                    <PiShareFat fontSize={25} color="#151515" />
                  )}
                </div>
              </div>
            </div>
          </div>

          <div className="col-lg-6 col-md-6 col-12 pt-lg-0 pt-md-0 pt-3">
            <Progressbar
              reviewDistribution={companyData?.company_profile?.review_distribution}
            />
          </div>
        </div>

        <div className="row pt-lg-5 pt-md-4 pt-3">
          <div className="col-lg-6 col-md-6 col-12">
            <h3 className="top_tags_h3">Top Tags</h3>
            <div className="d-flex flex-wrap gap-2 pt-2">
              {companyData?.company_profile?.top_positive_tags?.map((tag) => (
                <div
                  key={tag}
                  className="px-3 py-1"
                  style={{
                    backgroundColor: "#EDFDED",
                    color: "white",
                    cursor: "pointer",
                    borderRadius: "30px",
                    fontSize: "0.85em",
                    color: "#2C2C2C",
                    height: "28px",
                  }}
                >
                  {tag}
                </div>
              ))}
            </div>
            <div className="tags_divider mt-3 mb-2 ms-lg-4 ms-md-3 ms-3"></div>
            <div className="d-flex flex-wrap gap-2 pt-2">
              {companyData?.company_profile?.top_negative_tags?.map((tag) => (
                <div
                  key={tag}
                  className="px-3 py-1"
                  style={{
                    backgroundColor: "#FFD0CA",
                    color: "white",
                    cursor: "pointer",
                    borderRadius: "30px",
                    fontSize: "0.85em",
                    color: "#2C2C2C",
                    height: "28px",
                  }}
                >
                  {tag}
                </div>
              ))}
            </div>

            <div className="company_rank_box my-lg-5 my-md-4 my-3">
              <div className="py-3 px-3">
                <h3 className="company_rank_h3 pt-2">Company Rank</h3>
                <div className="mt-4">
                  <div id="parent-id">
                    <div className="">
                      <table>
                        <tbody>
                          <tr className="light-text">
                            <td>Rank</td>
                            <td>Name</td>
                            <td>Rating</td>
                          </tr>
                          <tr className="dark-text">
                            <td>
                              {companyData?.company_profile?.company_rank === 0
                                ? "Unranked"
                                : `${companyData?.company_profile?.company_rank}`}
                            </td>
                            <td>{companyData?.company_profile?.company_name}</td>
                            <td>{companyData?.company_profile?.company_rating}</td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="offset-lg-2 offset-md-2 col-lg-4 col-md-4 col-12">
            <div>
              <div className="company_rank_box my-lg-5 my-md-4 my-3 d-none">
                <div className="py-3 px-3">
                  <h3 className="company_rank_h3 pt-2">Similar Companies</h3>
                  <div className="px-lg-5 pt-2">
                    <div id="parent-id">
                      <div className="table-container">
                        <table>
                          <tbody>
                            <tr className="light_grey">
                              <td>Google</td>
                              <td>2.9</td>
                            </tr>
                            <tr className="light_grey">
                              <td>Meta</td>
                              <td>4.8</td>
                            </tr>
                            <tr className="light_grey">
                              <td>X</td>
                              <td>4.7</td>
                            </tr>
                            <tr className="light_grey">
                              <td>Airbnb</td>
                              <td>4.7</td>
                            </tr>
                            <tr className="light_grey">
                              <td>Oracle</td>
                              <td>4.6</td>
                            </tr>
                          </tbody>
                        </table>
                      </div>
                    </div>
                    <div className="d-flex justify-content-center pt-3">
                      <button className="view_more_btn">View More</button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Top Rated Managers Table */}
        <div className="row pt-lg-4 pt-md-4 pt-3">
          <div className="col-12">
            <div className="">
              <h3 className="top_rated_companies_h3">
                Top Rated Superstar Managers at{" "}
                {companyData?.company_profile?.company_name || ""}
              </h3>
            </div>
          </div>
        </div>
        <div className="row">
          <div className="table-responsive pt-2">
            <table className="table">
              <thead>
                <tr className="">
                  <th>Rank</th>
                  <th>Manager</th>
                  <th>
                    Rating <br /> <span className="out_of_five_p">Out of 5</span>
                  </th>
                  <th># of Reviews</th>
                  <th>Popular Tags</th>
                </tr>
              </thead>
              <tbody>
                {Object.entries(companyData?.company_profile?.top_managers || {}).map(
                  ([key, manager], index) =>
                    manager?.manager_full_name ? (
                      <tr
                        key={index}
                        style={{ backgroundColor: "white", borderBottom: "1px solid #CBCBCBA8" }}
                      >
                        <td>
                          <span className="company_rank_text">{key}</span>
                        </td>
                        <td>
                          <Link
                            to={`/manager?name=${encodeURIComponent(
                              manager.manager_full_name
                            )}&id=${manager.manager_id}`}
                            className="manager-name-link company_rating"
                            title={`View ${manager.manager_full_name}'s profile`}
                            aria-label={`View ${manager.manager_full_name}'s profile`}
                          >
                            {manager.manager_full_name}
                          </Link>
                        </td>
                        <td>
                          <span className="company_rating">{manager.rating}</span>
                        </td>
                        <td>
                          <span className="company_rating">
                            {manager.all_review_count?.toLocaleString()}
                          </span>
                        </td>
                        <td>
                          {Array.isArray(manager?.popular_tags) ? (
                            manager.popular_tags.map((tag, idx) => (
                              <span key={idx} className="tag me-2">
                                {tag}
                              </span>
                            ))
                          ) : (
                            <span className="tag">No tags</span>
                          )}
                        </td>
                      </tr>
                    ) : null
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Subscribe box */}
        <div className="row py-lg-5 py-md-4 py-3">
          <div className="offset-lg-2 col-lg-8 col-md-12 col-12">
            <div className="get_updates_box">
              <h4 className="get_updates_h4 pt-4 text-center">
                Stay updated with news about this company
              </h4>
              <div className="row py-lg-4 py-md-3 py-2">
                <Formik initialValues={initialValues} validationSchema={validationSchema} onSubmit={handleSubmit}>
                  {({ handleSubmit }) => (
                    <Form onSubmit={handleSubmit}>
                      <div className="row pt-4 pb-4">
                        {/* Input Fields */}
                        <div className="col-lg-6 col-md-6 col-12">
                          {/* Email (blocked) */}
                          <div className="mb-lg-0 mb-md-0 mb-3">
                            <Field
                              type="email"
                              id="email"
                              name="email"
                              className="form-control subscribe_email_input"
                              placeholder="Email"
                              readOnly
                              onFocus={handleComingSoon}
                              onClick={handleComingSoon}
                              onKeyDown={handleComingSoon}
                              onPaste={handleComingSoon}
                              autoComplete="off"
                              inputMode="none"
                            />
                            <ErrorMessage name="email" component="small" className="text-danger" />
                          </div>
                        </div>

                        {/* Subscribe Button (blocked) */}
                        <div className="col-lg-6 col-md-6 col-12 d-flex align-items-center justify-content-center">
                          <button
                            type="button"
                            className="subscribe_btn"
                            onClick={handleComingSoon}
                            aria-disabled="true"
                          >
                            Subscribe
                          </button>
                        </div>
                      </div>
                    </Form>
                  )}
                </Formik>
              </div>
            </div>
          </div>
        </div>
      </div>

      <CreateCompanyFooter />
    </div>
  );
};

export default CompanyProfile;